import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, ChevronRight, CheckCircle2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tank-replace-pro/artifacts/isbxf87e_Logo%20Kool%20Plumbing-01.png";
const SITE_URL = "https://koolplumbing.com";

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="text-2xl font-heading text-blue-900">{title}</h2>
    <div className="text-gray-600 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </div>
);

const GuidePage = () => {
  const canonical = `${SITE_URL}/resources/septic-replacement-guide`;
  return (
    <div className="flex-1 font-body bg-white" data-testid="guide-page">
      <Helmet>
        <title>The Florida Homeowner's Septic Replacement Guide | Kool Plumbing</title>
        <meta name="description" content="A free, complete guide to septic tank replacement in Miami-Dade & Broward: warning signs, costs, permitting, drain fields, and how to choose a contractor." />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <section className="bg-blue-950 text-white py-16">
        <div className="max-w-3xl mx-auto px-6 space-y-4">
          <nav className="text-xs text-blue-200 flex items-center gap-2">
            <Link to="/" className="hover:text-amber-400">Home</Link><ChevronRight size={12} /><span className="text-amber-400">Septic Replacement Guide</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading leading-tight">The Florida Homeowner's Septic Replacement Guide</h1>
          <p className="text-gray-300">Everything you need to know before replacing a septic system in Miami-Dade or Broward — from a Florida State Certified Plumbing Contractor (#CFC1432999).</p>
        </div>
      </section>

      <article className="py-16">
        <div className="max-w-3xl mx-auto px-6 space-y-12">
          <Section title="1. Seven warning signs your septic system is failing">
            <p>Catching septic problems early can save you thousands. Watch for these red flags:</p>
            <ul className="space-y-2">
              {["Sewage backing up into sinks, tubs, or toilets", "Standing water or soggy ground over the drain field", "Foul odors in the yard or near the tank", "Slow drains throughout the house", "Bright green, spongy grass above the septic area", "Gurgling sounds in the plumbing", "A system that's 25+ years old or repeatedly needs repair"].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 mt-0.5" /><span>{t}</span></li>
              ))}
            </ul>
          </Section>

          <Section title="2. What septic replacement really costs in South Florida">
            <p>Most residential septic tank replacements in Miami-Dade and Broward range from about <strong>$6,500 to $18,000</strong>. The final price depends on:</p>
            <ul className="space-y-2">
              {["Tank size (driven by the number of bedrooms in your home)", "Soil conditions and water table — coastal lots often need engineered systems", "Whether the drain field also needs replacement", "Permitting and engineering requirements", "Access and excavation complexity on your lot"].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 mt-0.5" /><span>{t}</span></li>
              ))}
            </ul>
            <p>The best way to get an accurate number is a free on-site inspection — or try our <Link to="/#calculator" className="text-blue-700 underline">online cost estimator</Link> for an instant range.</p>
          </Section>

          <Section title="3. How permitting & soil testing works">
            <p>Florida requires every septic replacement to be permitted and inspected. The process involves a <strong>soil percolation test</strong> to measure how quickly your soil absorbs water, which determines the size and type of drain field you need. Your contractor then submits engineered plans to the county for approval before any work begins.</p>
            <p>A good contractor handles all of this in-house — soil testing, engineering, permitting, and final inspection — so you never have to navigate county paperwork yourself.</p>
          </Section>

          <Section title="4. Conventional vs. aerobic vs. mound systems">
            <p>Not every property can use a standard system. In areas with high water tables (common near the coast) or poor soil, you may need an <strong>aerobic treatment unit</strong> or an elevated <strong>mound system</strong>. Choosing the right system upfront prevents premature failure and costly do-overs.</p>
          </Section>

          <Section title="5. Five questions to ask before hiring any contractor">
            <ul className="space-y-2">
              {["Are you a licensed Florida plumbing contractor? (Ask for the license number.)", "Do you handle permitting and soil testing in-house?", "Is your quote all-inclusive, or will excavation cost extra?", "What warranty do you offer on the tank and workmanship?", "Can you provide local references in my county?"].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 mt-0.5" /><span>{t}</span></li>
              ))}
            </ul>
          </Section>

          {/* CTA */}
          <div className="bg-blue-950 text-white rounded-2xl p-8 text-center space-y-4">
            <h3 className="text-2xl font-heading">Ready for a free, no-obligation quote?</h3>
            <p className="text-gray-300 text-sm">Kool Plumbing handles your entire septic replacement — testing, permits, and installation — across Miami-Dade &amp; Broward.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/services/septic-tank-replacement" className="px-7 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold transition">Get a Free Quote</Link>
              <a href="tel:+17866224963" className="px-7 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold transition flex items-center justify-center gap-2"><Phone size={16} /> (786) 622-4963</a>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-blue-950 text-white py-10">
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Kool Plumbing" className="h-12 w-auto" />
          <div className="text-xs text-gray-300 text-center sm:text-right space-y-1">
            <p className="flex items-center gap-2 justify-center sm:justify-end"><Phone size={13} className="text-amber-500" /> (786) 622-4963 · info@koolplumbing.com</p>
            <Link to="/" className="text-amber-500 hover:underline">← Back to homepage</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GuidePage;
